import { apiRequest } from "./client";

export type TutorAction =
  | "explain"
  | "explain_differently"
  | "similar_question"
  | "why_others_wrong"
  | "ask_specific"
  | "answer_attempt";

export interface TutorTurn {
  reply: string;
  nextActions: TutorAction[];
  exchangesUsed: number;
  exchangeLimit: number;
  sessionId: string;
}

export interface TutorThread {
  sessionId: string;
  turns: { role: "student" | "tutor"; content: string }[];
  exchangesUsed: number;
  exchangeLimit: number;
}

/**
 * One tutor turn, through the backend.
 *
 * Never straight to the ai_engine: it has no auth of its own, and the question
 * context is read from the database rather than trusted from here.
 */
export function tutorTurn(input: {
  questionId: string;
  action: TutorAction;
  studentAnswer?: string | null;
  studentMessage?: string;
}): Promise<TutorTurn> {
  return apiRequest<TutorTurn>("/api/tutor/turn", {
    method: "POST",
    body: input,
    authenticated: true,
  });
}

/** Redraw a conversation the student has had before, after a refresh. */
export function tutorThread(questionId: string): Promise<TutorThread> {
  return apiRequest<TutorThread>(`/api/tutor/thread/${questionId}`, { authenticated: true });
}

export interface TutorStarterQuestion {
  questionId: string;
  subjectId: string;
  subject: string;
  stem: string;
  source: string;
  studentAnswer: string | null;
  correctAnswer: string | null;
}

export interface TutorStarters {
  /**
   * Wrong answers grouped by subject, most recently missed subject first.
   *
   * Grouped rather than a flat list: the six most recent mistakes are usually
   * six of the same subject, so a student who came to ask about Biology had to
   * read past all of them.
   */
  bySubject: { subjectId: string; subject: string; questions: TutorStarterQuestion[] }[];
  weakTopics: { topicId: string; name: string; subjectId: string; subject: string }[];
}

/**
 * What the tutor can help with, for the tab's cold entry.
 *
 * The tutor is pinned to a question, so opening the tab without one needs
 * something real to pin to — the questions this student got wrong, and their
 * weak topics.
 */
export function tutorStarters(): Promise<TutorStarters> {
  return apiRequest<TutorStarters>("/api/tutor/starters", { authenticated: true });
}

export interface TheoryMarkResult {
  score: number;
  maxScore: number;
  feedback: string;
  overallFeedback: string;
  highlightedAnswer: string | null;
  /** False in v1: the mark is feedback, never a tracking input (ADR-0006). */
  readinessAffected: boolean;
}

/**
 * Mark a written answer against the subject's WAEC scheme.
 *
 * Through the backend, never straight to the engine: the question and its
 * model answer are read from our database, so a client cannot ask for a mark
 * against a question it made up.
 */
export function markTheory(input: {
  questionId: string;
  answer: string;
  englishPaper?: "essay" | "comprehension" | "letter";
}): Promise<TheoryMarkResult> {
  return apiRequest<TheoryMarkResult>("/api/tutor/mark-theory", {
    method: "POST",
    body: input,
    authenticated: true,
  });
}
